import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Entity, ListItem, Octopus } from '@maximizer/core/shared/domain';
import { ContextService } from './context.service';
import { EntityService } from './entity.service';
import { MetadataService } from './metadata.service';
import { map, Observable } from 'rxjs';
import { FeatureService } from './feature.service';

@Injectable()
export class AddressBookService extends EntityService {
  private useSqlWriteDriver?: boolean;

  constructor(
    http: HttpClient,
    context: ContextService,
    metadata: MetadataService,
    private readonly feature: FeatureService,
  ) {
    super(Octopus.AbEntryEntityCode, http, context, metadata);
  }

  get readDriver(): Octopus.RequestConfiguration {
    return Octopus.AbEntryReadDriver;
  }

  get writeDriver(): Octopus.RequestConfiguration {
    this.useSqlWriteDriver ??= this.feature.isFeatureOn(
      'core-abentry-use-sql-driver',
      false,
    );

    return this.useSqlWriteDriver
      ? Octopus.AbEntryWriteDriver
      : { Drivers: {} };
  }

  override getFieldsForDefaultEntry(metadata = true): Octopus.Scope<Entity> {
    return {
      [Octopus.AbEntryFields.LastName]: this.getValueAndDisplayScope(metadata),
    };
  }

  override getTypesScope(metadata = true): Octopus.Scope<Entity> {
    return {
      ...super.getTypesScope(metadata),
      AddressObject: this.getAddressScope(metadata),
      EmailField: this.getEmailScope(metadata),
      PhoneField: this.getPhoneScope(metadata),
      TerritoryObject: this.getKeyAndDisplayScope(metadata),
    };
  }

  getParentKey(key: string): Observable<string | null> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            ParentKey: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            if (result.AbEntry.Data) {
              return (
                result.AbEntry.Data[0].ParentKey?.Value ??
                result.AbEntry.Data[0].Key ??
                null
              );
            }
          }
          return null;
        }),
      );
  }

  getEntriesById(
    id: string[],
    isCompatibilityWithLastVersion: boolean = false,
  ): Observable<ListItem<string>[]> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $IN: id,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            DisplayValue: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
    };
    if (isCompatibilityWithLastVersion) {
      request.Compatibility = {
        AbEntryKey: '2.0',
      };
    }
    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data.map((entry) => {
              return {
                id: entry.Key ?? '',
                name: entry.DisplayValue,
              };
            });
          }
          return [];
        }),
      );
  }

  getEntryByKey(key: string): Observable<Entity[]> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: key,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            FirstName: 1,
            LastName: 1,
            FullName: 1,
            Type: 1,
            ParentKey: 1,
            DisplayValue: 1,
          },
        },
      },
      Compatibility: {
        AbEntryKey: '2.0',
      },
      Configuration: Octopus.AbEntryReadDriver,
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data;
          }
          return [];
        }),
      );
  }

  getParentOrAllRelatedEntries(
    key: string[],
    loadRelatedEntries?: boolean,
  ): Observable<Entity[]> {
    let query: Octopus.SearchQuery<Octopus.AbEntry> = {
      Key: {
        $EQ: {
          Body: key[0],
          Parent: true,
        },
      },
    };
    if (loadRelatedEntries) {
      query = {
        Key: {
          $IN: key,
        },
      };
    }
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: query,
        },
        Scope: {
          Fields: {
            Key: 1,
            Type: 1,
            ParentKey: 1,
            FullName: 1,
            FirstName: 1,
            LastName: 1,
            Phone: 1,
            Email: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Compatibility: {
        AbEntryKey: '2.0',
      },
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data;
          }
          return [];
        }),
      );
  }

  getParentDetails(parentKey: string): Observable<Entity | null> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $EQ: parentKey,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            Type: 1,
            Department: 1,
            Division: 1,
            Phone: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone1: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone2: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone3: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            Phone4: {
              Extension: 1,
              Number: 1,
              Description: 1,
            },
            WebSite: 1,
            SecAccess: {
              Read: [
                {
                  Key: 1,
                  DisplayValue: 1,
                },
              ],
              Write: [
                {
                  Key: 1,
                  DisplayValue: 1,
                },
              ],
            },
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data?.length ? result.AbEntry.Data[0] : null;
          }
          return null;
        }),
      );
  }

  getReportsTo(parentKey: string): Observable<ListItem<string | null>[]> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            ParentKey: {
              $EQ: parentKey,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            FullName: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Compatibility: {
        AbEntryKey: '2.0',
      },
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data.map((entry) => {
              return {
                id: entry.Key ?? null,
                name: entry.FullName,
              };
            });
          }
          return [];
        }),
      );
  }

  getContacts(individualKey: string): Observable<Entity[]> {
    const query: Octopus.SearchQuery<Octopus.AbEntry> = {
      ParentKey: {
        $EQ: individualKey,
      },
    };

    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: query,
        },
        Scope: {
          Fields: {
            Key: 1,
            Type: 1,
            ParentKey: 1,
            FullName: 1,
            FirstName: 1,
            LastName: 1,
            Phone: 1,
            Email: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Compatibility: {
        AbEntryKey: '2.0',
      },
    };

    return this.http
      .post<Octopus.AbEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry.Data;
          }
          return [];
        }),
      );
  }

  getContactFamilyTrees(key: string): Observable<string[]> {
    const query: Octopus.SearchQuery<Octopus.FamilyTree> = {
      ContactKey: {
        $EQ: key,
      },
    };

    const request: Octopus.FamilyTreeReadRequest = {
      FamilyTreeRoots: {
        Criteria: { SearchQuery: query },
        Scope: {
          Fields: {},
          Types: {
            '*': 1,
          },
        },
      },
      Compatibility: { AbEntryKey: '2.0' },
    };

    return this.http
      .post<Octopus.FamilyTreeResponse>(
        `${this.context.api}${Octopus.Action.READ_FAMILY_TREE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return (result.FamilyTreeRoots?.Data ?? []).map(
              (entry) => entry[Octopus.FamilyTreeFields.AbEntryKey] as string,
            );
          }
          return [];
        }),
      );
  }

  getEntriesByKeysWithField(
    keys: string[],
    field?: ListItem<string>,
  ): Observable<Entity[]> {
    const request: Octopus.AbEntryReadRequest = {
      AbEntry: {
        Criteria: {
          SearchQuery: {
            Key: {
              $IN: keys,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            DisplayValue: 1,
          },
        },
      },
      Configuration: Octopus.AbEntryReadDriver,
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: {
        AbEntryKey: '2.0',
      },
    };
    if (field) {
      request.AbEntry.Scope.Fields[field.id] = {
        DisplayValue: 1,
      };
    }
    return this.http
      .post<Octopus.EntityResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.AbEntry?.Data ?? [];
          }
          return [];
        }),
      );
  }
}
